import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
  AppState,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { subscriptionService } from '../services/subscriptionService';
import { fetchSubscriptionPrices, SubscriptionPrices } from '../services/subscriptionPriceService';
import { SUBSCRIPTION_LIMITS } from '../types/subscription';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';
import AppLoadingScreen from '../components/AppLoadingScreen';
import { createThemedStyles } from '../theme/useThemedStyles';

import { isWeb } from '../utils/platformUtils';
import SubscriptionLegalLinks from '../components/SubscriptionLegalLinks';
import {
  IOSSubscriptionStoreView,
  isIOSSubscriptionStoreAvailable,
} from 'goalmint-subscription-store';
import { SUBSCRIPTION_PRODUCT_IDS, IOS_SUBSCRIPTION_GROUP_ID, isRevenueCatWebConfigured } from '../config/revenuecat';
import { showAlert } from '../utils/webAlert';
import { ensureRevenueCatCheckoutTarget } from '../utils/revenueCatCheckoutTarget';

const SubscriptionScreen: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentTier, getMaxGoals, getFeatures, updateSubscription, subscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [prices, setPrices] = useState<SubscriptionPrices | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<{ [key: string]: 'monthly' | 'yearly' }>({
    standard: 'monthly',
    premium: 'monthly',
  });
  const [iosStoreAvailable, setIosStoreAvailable] = useState(false);
  const styles = useSubscriptionStyles();
  const syncInFlightRef = useRef(false);

  const syncStoreSubscription = useCallback(async () => {
    if (!isAuthenticated || !user?.uid || isWeb || syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    try {
      const synced = await subscriptionService.syncSubscriptionFromStoreWithRetry(user.uid);
      if (synced) {
        await updateSubscription(synced);
      }
    } catch (error) {
      console.warn('Store subscription sync failed:', error);
    } finally {
      syncInFlightRef.current = false;
    }
  }, [isAuthenticated, user?.uid, updateSubscription]);

  useEffect(() => {
    if (Platform.OS === 'ios') {
      setIosStoreAvailable(isIOSSubscriptionStoreAvailable());
    }
    if (isWeb) {
      ensureRevenueCatCheckoutTarget();
    }
  }, []);

  const loadProducts = async () => {
    setProductsLoading(true);
    setPurchaseError(null);
    try {
      if (isWeb && isRevenueCatWebConfigured() && user?.uid) {
        await subscriptionService.ensureRevenueCatReady(user.uid);
      }
      const fetched = await subscriptionService.getAvailableProducts();
      setProducts(fetched);
    } catch (e) {
      console.warn('Failed to fetch products, using fallback:', e);
      setProducts([
        {
          id: 'standard_monthly',
          tier: 'standard',
          platform: 'web',
          price: 4.99,
          currency: 'USD',
          billingPeriod: 'monthly',
          trialDays: 7,
          description: 'Standard Plan (Monthly) - Fallback',
        },
        {
          id: 'standard_yearly',
          tier: 'standard',
          platform: 'web',
          price: 49.99,
          currency: 'USD',
          billingPeriod: 'yearly',
          trialDays: 7,
          description: 'Standard Plan (Yearly) - Fallback',
        },
        {
          id: 'premium_monthly',
          tier: 'premium',
          platform: 'web',
          price: 9.99,
          currency: 'USD',
          billingPeriod: 'monthly',
          trialDays: 7,
          description: 'Premium Plan (Monthly) - Fallback',
        },
        {
          id: 'premium_yearly',
          tier: 'premium',
          platform: 'web',
          price: 99.99,
          currency: 'USD',
          billingPeriod: 'yearly',
          trialDays: 7,
          description: 'Premium Plan (Yearly) - Fallback',
        },
      ]);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (!isWeb || !isRevenueCatWebConfigured()) {
      loadProducts();
      return;
    }
    if (isAuthenticated && user?.uid) {
      loadProducts();
    }
  }, [isAuthenticated, user?.uid]);

  useEffect(() => {
    setProductsLoading(true);
    fetchSubscriptionPrices()
      .then(setPrices)
      .catch(() => setPrices(null))
      .finally(() => setProductsLoading(false));
  }, []);

  // After App Store (SubscriptionStoreView) purchase, pull entitlements into Firestore.
  useFocusEffect(
    useCallback(() => {
      syncStoreSubscription();
    }, [syncStoreSubscription])
  );

  // SubscriptionStoreView purchases complete in Apple's sheet — sync when app returns.
  useEffect(() => {
    if (isWeb) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        syncStoreSubscription();
      }
    });
    return () => sub.remove();
  }, [syncStoreSubscription]);

  const handlePurchase = async (product: { id: string; revenueCatPackage?: unknown }) => {
    const productId = product.id;
    console.log('🔧 SUBSCRIPTION: handlePurchase called with productId:', productId);
    setPurchaseError(null);
    setIsLoading(true);
    setSelectedProduct(productId);
    try {
      if (isWeb && !isAuthenticated) {
        const message = 'Sign in to subscribe.';
        setPurchaseError(message);
        showAlert('Sign in required', message);
        return;
      }

      console.log('🔧 SUBSCRIPTION: Calling subscriptionService.purchaseSubscription...');
      const result = await subscriptionService.purchaseSubscription(productId, {
        revenueCatPackage: product.revenueCatPackage as import('@revenuecat/purchases-js').Package | undefined,
      });
      console.log('🔧 SUBSCRIPTION: purchaseSubscription result:', result);
      if (result.success && result.subscription) {
        await updateSubscription(result.subscription);

        const tier = result.subscription.tier;
        const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);

        showAlert(
          'Success!',
          `Your subscription has been activated. Enjoy your ${tierName} features!`
        );
      } else if (result.error !== 'Purchase cancelled') {
        setPurchaseError(result.error || 'Unable to complete purchase. Please try again.');
        showAlert(
          'Purchase Failed',
          result.error || 'Unable to complete purchase. Please try again.'
        );
      }
    } catch (error) {
      console.error('🔧 SUBSCRIPTION: Purchase error:', error);
      const message =
        error instanceof Error ? error.message : 'An error occurred during purchase. Please try again.';
      setPurchaseError(message);
      showAlert('Error', message);
    } finally {
      setIsLoading(false);
      setSelectedProduct(null);
    }
  };

  const handleRestore = async () => {
    setIsLoading(true);
    
    try {
      const result = await subscriptionService.restorePurchases();
      
      if (result.success && result.subscription) {
        await updateSubscription(result.subscription);
        Alert.alert(
          'Restored!',
          'Your previous subscription has been restored.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'No Subscription Found',
          'No active subscription was found to restore.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Restore error:', error);
      Alert.alert(
        'Error',
        'Unable to restore purchases. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderFeature = (feature: string, index: number) => (
    <View key={index} style={styles.featureRow}>
      <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
      <Text style={styles.featureText}>{feature}</Text>
    </View>
  );

  // Helper to get product by tier and billing period
  const getProductByTierAndPeriod = (tier: 'standard' | 'premium', period: 'monthly' | 'yearly') => {
    return products.find(p => p.tier === tier && p.billingPeriod === period);
  };

  // Helper to get price by tier and billing period
  const getPriceByTierAndPeriod = (tier: 'standard' | 'premium', period: 'monthly' | 'yearly') => {
    const product = getProductByTierAndPeriod(tier, period);
    if (product?.localizedPrice) {
      return { localizedPrice: product.localizedPrice };
    }
    if (!prices) return null;
    const key = `${tier}_${period}` as keyof SubscriptionPrices;
    return prices[key];
  };

  // Block access to Standard/Premium features if subscription is expired or canceled
  const isActive = subscription?.status === 'active' || subscription?.status === 'trial';
  const effectiveTier = isActive ? currentTier : 'free';

  // Calculate trial information
  const getTrialInfo = () => {
    if (subscription?.status === 'trial' && subscription.trialEndDate) {
      const trialEnd = new Date(subscription.trialEndDate);
      const now = new Date();
      const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining > 0) {
        return {
          isInTrial: true,
          daysRemaining,
          trialEndDate: subscription.trialEndDate
        };
      }
    }
    return { isInTrial: false, daysRemaining: 0, trialEndDate: null };
  };

  const trialInfo = getTrialInfo();

  const renderTierCard = (tier: 'free' | 'standard' | 'premium') => {
    const limits = SUBSCRIPTION_LIMITS[tier];
    const isCurrentTier = effectiveTier === tier;
    let product: any = undefined;
    let selectedPeriod: 'monthly' | 'yearly' = 'monthly';
    if (tier !== 'free') {
      selectedPeriod = billingPeriod[tier];
      product = getProductByTierAndPeriod(tier, selectedPeriod);
    }
    const isSelected = selectedProduct === product?.id;

    return (
      <View key={tier} style={[styles.tierCard, isWeb && styles.tierCardWeb, isCurrentTier && styles.currentTierCard]}>
        <LinearGradient
          colors={
            tier === 'premium' 
              ? ['#1976D2', '#1565C0'] 
              : tier === 'standard' 
                ? ['#4CAF50', '#45a049'] 
                : ['#9E9E9E', '#757575']
          }
          style={styles.tierHeader}
        >
          <Text style={styles.tierTitle}>
            {tier.charAt(0).toUpperCase() + tier.slice(1)}
          </Text>
          {isCurrentTier && (
            <View style={styles.currentBadge}>
              <Text style={styles.currentBadgeText}>Current</Text>
            </View>
          )}
        </LinearGradient>

        <View style={styles.tierContent}>
          <Text style={styles.goalLimit}>
            Up to {limits.maxGoals} Goals
          </Text>
          {/* Billing period toggle for paid plans */}
          {tier !== 'free' && (
            <View style={styles.billingToggleContainer}>
              <TouchableOpacity
                style={[styles.billingToggle, selectedPeriod === 'monthly' && styles.billingToggleSelected]}
                onPress={() => setBillingPeriod(prev => ({ ...prev, [tier]: 'monthly' }))}
              >
                <Text style={[styles.billingToggleText, selectedPeriod === 'monthly' && styles.billingToggleTextSelected]}>Monthly</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.billingToggle, selectedPeriod === 'yearly' && styles.billingToggleSelected]}
                onPress={() => setBillingPeriod(prev => ({ ...prev, [tier]: 'yearly' }))}
              >
                <Text style={[styles.billingToggleText, selectedPeriod === 'yearly' && styles.billingToggleTextSelected]}>Yearly</Text>
              </TouchableOpacity>
            </View>
          )}
          <Text style={styles.price}>
            {tier === 'free' ? 'Free' : (() => {
              const priceObj = getPriceByTierAndPeriod(tier, selectedPeriod);
              if (productsLoading) return 'Loading...';
              if (!priceObj) return 'Unavailable';
              return `${priceObj.localizedPrice} / ${selectedPeriod}`;
            })()}
          </Text>
          {/* Show trial info if available */}
          {tier !== 'free' && product?.trialDays && (
            <Text style={styles.trialInfo}>{product.trialDays}-day free trial</Text>
          )}
          <View style={styles.featuresContainer}>
            {limits.features.map(renderFeature)}
          </View>
          {tier !== 'free' && !(Platform.OS === 'ios' && iosStoreAvailable) && (
            <TouchableOpacity
              style={[
                styles.purchaseButton,
                tier === 'premium' && styles.premiumPurchaseButton,
                tier === 'standard' && styles.standardPurchaseButton,
                isSelected && styles.purchaseButtonLoading,
                isCurrentTier && styles.currentTierButton
              ]}
              onPress={() => product && handlePurchase(product)}
              disabled={isLoading || isCurrentTier || !product}
            >
              {isLoading && isSelected ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.purchaseButtonText}>
                  {isCurrentTier ? 'Current Plan' : 'Subscribe'}
                </Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  // Helper to render checkmark or X for Yes/No
  const renderYesNoIcon = (value: string) => {
    if (value === 'Yes') {
      return <Ionicons name="checkmark-circle" size={20} color="#4CAF50" style={{ alignSelf: 'center' }} />;
    }
    if (value === 'No') {
      return <Ionicons name="close-circle" size={20} color="#F44336" style={{ alignSelf: 'center' }} />;
    }
    return <Text style={styles.compareTableCellText}>{value}</Text>;
  };

  if (productsLoading) {
    return <AppLoadingScreen message="Loading subscription plans..." />;
  }

  return (
    <WebPage title="Subscription">
    <PageScroll style={[styles.container, isWeb && styles.containerWebScroll]}>
      <View style={styles.header}>
        <Text style={styles.title}>Choose Your Plan</Text>
        <Text style={styles.subtitle}>
          Unlock more goals and features to accelerate your financial journey
        </Text>
        {purchaseError ? (
          <Text style={styles.purchaseErrorText}>{purchaseError}</Text>
        ) : null}
        {isWeb && isRevenueCatWebConfigured() && !products.some((p) => p.revenueCatPackage) ? (
          <Text style={styles.purchaseHintText}>
            Showing default prices — RevenueCat web products did not load. Check the browser console
            and your Web Billing offering in RevenueCat.
          </Text>
        ) : null}
      </View>

      {/* Trial Banner */}
      {trialInfo.isInTrial && (
        <View style={styles.trialBanner}>
          <Ionicons name="time-outline" size={24} color="#fff" />
          <View style={styles.trialBannerContent}>
            <Text style={styles.trialBannerTitle}>Free Trial Active</Text>
            <Text style={styles.trialBannerText}>
              {trialInfo.daysRemaining} day{trialInfo.daysRemaining !== 1 ? 's' : ''} remaining
            </Text>
          </View>
        </View>
      )}

      {Platform.OS === 'ios' && iosStoreAvailable && (
        <View style={styles.storeKitSection}>
          <Text style={styles.storeKitTitle}>Subscribe with App Store</Text>
          <Text style={styles.storeKitSubtitle}>
            Manage billing, trials, and renewals with Apple&apos;s secure subscription flow.
          </Text>
          <IOSSubscriptionStoreView
            productIds={[...SUBSCRIPTION_PRODUCT_IDS]}
            subscriptionGroupId={IOS_SUBSCRIPTION_GROUP_ID || undefined}
            style={styles.storeKitView}
          />
        </View>
      )}

      <View style={[styles.tiersContainer, isWeb && styles.tiersContainerWeb]}>
        {renderTierCard('free')}
        {renderTierCard('standard')}
        {renderTierCard('premium')}
      </View>

      {/* Compare Plan Table */}
      <View style={styles.compareTableContainer}>
        <Text style={styles.compareTableTitle}>Compare Plans</Text>
        <View style={styles.compareTableHeader}>
          <Text style={[styles.compareTableCell, styles.compareTableHeaderCell]}>Features</Text>
          <Text style={[styles.compareTableCell, styles.compareTableHeaderCell]}>Free</Text>
          <Text style={[styles.compareTableCell, styles.compareTableHeaderCell]}>Standard</Text>
          <Text style={[styles.compareTableCell, styles.compareTableHeaderCell]}>Premium</Text>
        </View>
        {/* Features to compare */}
        {[
          { label: 'Max Goals', key: 'maxGoals', render: (tier) => SUBSCRIPTION_LIMITS[tier].maxGoals.toString(), premium: '25', standard: SUBSCRIPTION_LIMITS['standard'].maxGoals.toString(), free: SUBSCRIPTION_LIMITS['free'].maxGoals.toString() },
          { label: 'Basic Goal Tracking', key: 'basicGoalTracking', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Basic goal tracking') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'Yes' },
          { label: 'Progress Monitoring', key: 'progressMonitoring', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Progress monitoring') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'Yes' },
          { label: 'Basic Dashboard', key: 'basicDashboard', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Basic dashboard') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'Yes' },
          { label: 'Goal Type Summaries', key: 'goalTypeSummaries', render: (tier) => (tier === 'standard' || tier === 'premium') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Ad-free', key: 'showAds', render: (tier) => SUBSCRIPTION_LIMITS[tier].showAds ? 'No' : 'Yes', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'CSV Data Export', key: 'csvExport', render: (tier) => SUBSCRIPTION_LIMITS[tier].exportFormats.includes('csv') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Enhanced Analytics', key: 'enhancedAnalytics', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Enhanced analytics') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Goal Performance Insights', key: 'goalPerformance', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Goal performance insights') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Financial Analysis', key: 'financialAnalysis', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Financial analysis') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Smart Recommendations', key: 'smartRecommendations', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Smart recommendations') ? 'Yes' : 'No', premium: 'Yes', standard: 'Yes', free: 'No' },
          { label: 'Excel & PDF Exports', key: 'excelPdfExport', render: (tier) => (SUBSCRIPTION_LIMITS[tier].exportFormats.includes('excel') || SUBSCRIPTION_LIMITS[tier].exportFormats.includes('pdf')) ? 'Yes' : 'No', premium: 'Yes', standard: 'No', free: 'No' },
          { label: 'Investment Tracking', key: 'investmentTracking', render: (tier) => (tier === 'premium') ? 'Yes' : 'No', premium: 'Yes', standard: 'No', free: 'No' },
          { label: 'Detailed Goal Analytics', key: 'detailedGoalAnalytics', render: (tier) => SUBSCRIPTION_LIMITS[tier].features.includes('Detailed goal analytics') ? 'Yes' : 'No', premium: 'Yes', standard: 'No', free: 'No' },
        ].map((row, idx) => (
          <View key={row.key} style={[styles.compareTableRow, idx % 2 === 0 && styles.compareTableRowAlt]}> 
            <Text style={[styles.compareTableCell, styles.compareTableLabel]}>{row.label}</Text>
            <View style={styles.compareTableCell}>{row.free !== undefined ? renderYesNoIcon(row.free) : renderYesNoIcon(row.render('free'))}</View>
            <View style={styles.compareTableCell}>{row.standard !== undefined ? renderYesNoIcon(row.standard) : renderYesNoIcon(row.render('standard'))}</View>
            <View style={styles.compareTableCell}>{row.premium !== undefined ? (row.key === 'maxGoals' ? <Text style={styles.compareTableCellText}>{row.premium}</Text> : renderYesNoIcon(row.premium)) : renderYesNoIcon(row.render('premium'))}</View>
          </View>
        ))}
      </View>

      {Platform.OS !== 'web' || isRevenueCatWebConfigured() ? (
        <TouchableOpacity
          style={styles.restoreButton}
          onPress={handleRestore}
          disabled={isLoading}
        >
          <Text style={styles.restoreButtonText}>Restore Purchases</Text>
        </TouchableOpacity>
      ) : null}

      {/* Manage/Cancel Subscription Button */}
      <TouchableOpacity
        style={styles.cancelButton}
        onPress={async () => {
          const managementUrl = await subscriptionService.getSubscriptionManagementUrl();
          const url =
            managementUrl ??
            (Platform.OS === 'ios'
              ? 'https://apps.apple.com/account/subscriptions'
              : 'https://play.google.com/store/account/subscriptions');
          Linking.openURL(url);
        }}
      >
        <Text style={styles.cancelButtonText}>Manage/Cancel Subscription</Text>
      </TouchableOpacity>

      <SubscriptionLegalLinks />

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          • Subscriptions automatically renew unless cancelled
        </Text>
        <Text style={styles.footerText}>
          • Cancel anytime in your device settings
        </Text>
        <Text style={styles.footerText}>
          • Free trial available for new subscribers
        </Text>
      </View>
    </PageScroll>
    </WebPage>
  );
};

const useSubscriptionStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.default,
  },
  containerWebScroll: {
    flexGrow: 0,
    flexShrink: 0,
  },
  header: {
    padding: 20,
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 22,
  },
  purchaseErrorText: {
    marginTop: 12,
    fontSize: 14,
    color: colors.error.main,
    textAlign: 'center',
    lineHeight: 20,
  },
  purchaseHintText: {
    marginTop: 8,
    fontSize: 12,
    color: colors.warning?.main ?? colors.text.secondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  tiersContainer: {
    padding: 20,
    gap: 20,
  },
  tiersContainerWeb: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'stretch',
    paddingHorizontal: 0,
  },
  tierCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    overflow: 'hidden',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tierCardWeb: {
    flex: 1,
    minWidth: 280,
    maxWidth: 360,
  },
  currentTierCard: {
    borderWidth: 2,
    borderColor: colors.success.main,
  },
  tierHeader: {
    padding: 20,
    alignItems: 'center',
    position: 'relative',
  },
  tierTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  currentBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  currentBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.success.main,
  },
  tierContent: {
    padding: 20,
  },
  goalLimit: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: 10,
  },
  price: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colors.success.main,
    textAlign: 'center',
    marginBottom: 20,
  },
  featuresContainer: {
    marginBottom: 20,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: colors.text.secondary,
    marginLeft: 10,
  },
  purchaseButton: {
    backgroundColor: colors.success.main,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  premiumPurchaseButton: {
    backgroundColor: colors.secondary.main,
  },
  standardPurchaseButton: {
    backgroundColor: colors.success.main,
  },
  purchaseButtonLoading: {
    backgroundColor: colors.grey[300],
  },
  currentTierButton: {
    backgroundColor: colors.grey[300],
  },
  purchaseButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  restoreButton: {
    margin: 20,
    padding: 15,
    backgroundColor: colors.grey[100],
    borderRadius: 8,
    alignItems: 'center',
  },
  restoreButtonText: {
    color: colors.text.secondary,
    fontSize: 16,
  },
  footer: {
    padding: 20,
    backgroundColor: colors.surface,
    margin: 20,
    borderRadius: 8,
  },
  storeKitSection: {
    marginHorizontal: 16,
    marginBottom: 8,
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
  },
  storeKitTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: 4,
  },
  storeKitSubtitle: {
    fontSize: 13,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: 12,
    paddingHorizontal: 8,
  },
  storeKitView: {
    width: '100%',
    minHeight: 520,
  },
  footerText: {
    fontSize: 12,
    color: colors.grey[500],
    marginBottom: 5,
  },
  compareTableContainer: {
    marginTop: 32,
    marginHorizontal: 16,
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  compareTableTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    color: colors.text.primary,
    textAlign: 'center',
  },
  compareTableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    marginBottom: 4,
  },
  compareTableHeaderCell: {
    fontWeight: 'bold',
    color: colors.text.secondary,
  },
  compareTableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
  },
  compareTableRowAlt: {
    backgroundColor: colors.grey[50],
  },
  compareTableCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: 14,
    color: colors.text.primary,
    flexDirection: 'row',
  },
  compareTableCellText: {
    fontSize: 14,
    color: colors.text.primary,
    textAlign: 'center',
  },
  compareTableLabel: {
    textAlign: 'left',
    fontWeight: '600',
    color: colors.text.primary,
  },
  cancelButton: {
    marginTop: 32,
    paddingVertical: 10,
    paddingHorizontal: 36,
    backgroundColor: colors.error.main,
    borderRadius: 8,
    alignItems: 'center',
    alignSelf: 'center',
    minWidth: 220,
    maxWidth: 320,
  },
  cancelButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  trialBanner: {
    padding: 10,
    backgroundColor: colors.success.main,
    flexDirection: 'row',
    alignItems: 'center',
  },
  trialBannerContent: {
    marginLeft: 10,
  },
  trialBannerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  trialBannerText: {
    fontSize: 14,
    color: '#fff',
  },
  billingToggleContainer: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  billingToggle: {
    padding: 8,
    borderWidth: 1,
    borderColor: colors.success.main,
    borderRadius: 8,
  },
  billingToggleSelected: {
    backgroundColor: colors.success.main,
  },
  billingToggleText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.success.main,
  },
  billingToggleTextSelected: {
    color: '#fff',
  },
  trialInfo: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 10,
  },
}));

export default SubscriptionScreen; 